import { RewardType, type ChallengeRewardConfigDto, type I18NText } from '@core/api/ChallengeClient';
import type { CdnObjectDto } from '@core/api/FitnessActivityClient';
import { autodisposer } from '@core/reactions';
import { injectTSDI } from '@core/tsdi';
import { FieldState, FormState } from 'formstate';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { component } from 'tsdi';
import { ChallengeDataStore } from '../data-store';
import {
    formErrors,
    isFormStateDirty,
    makeFormClean
} from '@components/utils/form';
import { bind } from 'lodash-decorators';
import { StepStatus } from '../store';
import { wrapRequest } from 'mobx-wrap-request';
import { Api } from '@core/api';
import { getCdnImage } from '@components/utils/cdn-image';

export type RewardFormType = 'NONE' | 'BADGE' | 'NEW_BADGE';

@component
export class ChallengeRewardStore {
    constructor() {
        makeObservable(this, {
            form: observable,
            selectedBadgeLabel: observable,
            createdBadgeTypeId: observable,
            fields: computed,
            errors: computed,
            isActive: observable,
            state: observable,
            onCollapsedToggle: action.bound,
            setRewardType: action.bound,
            setBadge: action.bound,
            createChallengeBadgeFromSnapshot: action
        });
    }

    public form = this.generateForm();
    public selectedBadgeLabel = '';
    public createdBadgeTypeId: number | undefined = undefined;
    public state: StepStatus = StepStatus.CREATING;
    public isActive = false;

    private get challengeDataStore() {
        return injectTSDI(ChallengeDataStore);
    }

    private get challengeClient() {
        return injectTSDI(Api).challengeClient;
    }

    private get fitnessActivityClient() {
        return injectTSDI(Api).fitnessActivityClient;
    }

    private generateForm() {
        return new FormState({
            rewardType: new FieldState<RewardFormType>('NONE'),
            badgeTypeId: new FieldState<number | undefined>(undefined),
            badgeLevelIndex: new FieldState<number>(0),
            badgeName: new FieldState<I18NText | undefined>(undefined),
            badgeImageUrl: new FieldState<string | undefined>(undefined),
            newBadgeName: new FieldState<I18NText>({} as I18NText),
            newBadgeDescription: new FieldState<I18NText | undefined>(undefined),
            newBadgeImage: new FieldState<CdnObjectDto | undefined>(undefined)
        });
    }

    public get fields() {
        return {
            rewardType: this.form.$.rewardType,
            badgeTypeId: this.form.$.badgeTypeId,
            badgeLevelIndex: this.form.$.badgeLevelIndex,
            badgeName: this.form.$.badgeName,
            badgeImageUrl: this.form.$.badgeImageUrl,
            newBadgeName: this.form.$.newBadgeName,
            newBadgeDescription: this.form.$.newBadgeDescription,
            newBadgeImage: this.form.$.newBadgeImage
        };
    }

    public get errors() {
        return formErrors(this.form);
    }

    public get formIsDirty() {
        return isFormStateDirty(this.form.$);
    }

    public onCollapsedToggle() {
        this.isActive = !this.isActive;
    }

    public setRewardType(type: RewardFormType, challengeName?: I18NText) {
        this.form.$.rewardType.onChange(type);
        if (type === 'NONE') {
            this.form.$.badgeTypeId.onChange(undefined);
            this.form.$.badgeLevelIndex.onChange(0);
            this.form.$.badgeName.onChange(undefined);
            this.form.$.badgeImageUrl.onChange(undefined);
            this.form.$.newBadgeName.onChange({} as I18NText);
            this.form.$.newBadgeDescription.onChange(undefined);
            this.form.$.newBadgeImage.onChange(undefined);
            this.selectedBadgeLabel = '';
        } else if (type === 'BADGE') {
            this.form.$.newBadgeName.onChange({} as I18NText);
            this.form.$.newBadgeDescription.onChange(undefined);
            this.form.$.newBadgeImage.onChange(undefined);
        } else if (type === 'NEW_BADGE') {
            this.form.$.badgeTypeId.onChange(undefined);
            this.form.$.badgeLevelIndex.onChange(0);
            this.form.$.badgeName.onChange(undefined);
            this.form.$.badgeImageUrl.onChange(undefined);
            this.selectedBadgeLabel = '';
            // One-time prefill from challenge name
            const currentName = this.form.$.newBadgeName.value;
            const isEmpty = !currentName || Object.keys(currentName).length === 0;
            if (isEmpty && challengeName) {
                this.form.$.newBadgeName.onChange(challengeName);
            }
        }
        this.validateReward(false);
    }

    public setBadge(badge: {
        id: number;
        name: Record<string, string>;
        levelIndex: number;
        imageUrl?: string;
        label: string;
    } | null) {
        if (badge) {
            this.form.$.badgeTypeId.onChange(badge.id);
            this.form.$.badgeLevelIndex.onChange(badge.levelIndex);
            this.form.$.badgeName.onChange(badge.name as I18NText);
            this.form.$.badgeImageUrl.onChange(badge.imageUrl);
            this.selectedBadgeLabel = badge.label;
        } else {
            this.form.$.badgeTypeId.onChange(undefined);
            this.form.$.badgeLevelIndex.onChange(0);
            this.form.$.badgeName.onChange(undefined);
            this.form.$.badgeImageUrl.onChange(undefined);
            this.selectedBadgeLabel = '';
        }
        this.validateReward(false);
    }

    @bind
    public async validateReward(onErrorOnly = false) {
        if (onErrorOnly) {
            if (this.state === StepStatus.ERROR) {
                const hasError = this.checkValidation();
                return this.adjustState(hasError);
            }
            this.state = StepStatus.MODIFIED;
            return false;
        }

        const hasError = this.checkValidation();
        return this.adjustState(hasError);
    }

    private checkValidation(): boolean {
        const rewardType = this.form.$.rewardType.value;
        if (rewardType === 'BADGE') {
            if (!this.form.$.badgeTypeId.value) {
                this.form.$.badgeTypeId.setError(
                    'Badge is required when reward type is Badge'
                );
                return true;
            }
        } else if (rewardType === 'NEW_BADGE') {
            const name = this.form.$.newBadgeName.value;
            const hasName = name && Object.values(name).some((v) => v?.trim());
            if (!hasName) {
                this.form.$.newBadgeName.setError(
                    'Badge name is required'
                );
                return true;
            }
            // Image required only for new badges; existing badges already have badgeImageUrl
            const hasExistingImage = !!this.form.$.badgeImageUrl.value;
            if (!this.form.$.newBadgeImage.value && !hasExistingImage) {
                this.form.$.newBadgeImage.setError(
                    'Badge image is required'
                );
                return true;
            }
            this.form.$.newBadgeImage.setError(undefined as any);
        }
        this.form.$.badgeTypeId.setError(undefined as any);
        this.form.$.newBadgeName.setError(undefined as any);
        return false;
    }

    public adjustState(hasError: boolean) {
        if (hasError) {
            this.state = StepStatus.ERROR;
            return true;
        } else if (
            this.challengeDataStore.challengeConfiguration === undefined ||
            this.state === StepStatus.CREATING
        ) {
            this.state = StepStatus.CREATING;
            return false;
        } else if (this.formIsDirty) {
            this.state = StepStatus.MODIFIED;
            return false;
        }

        this.state = StepStatus.COMPLETED;
        makeFormClean(this.form);
        return false;
    }

    public generateRequestData(): ChallengeRewardConfigDto | undefined {
        const rewardType = this.form.$.rewardType.value;
        if (rewardType === 'NONE' || rewardType === 'NEW_BADGE') {
            return undefined;
        }

        const badgeLevelIndex = this.form.$.badgeLevelIndex.value;

        return {
            rewardType: RewardType.BADGE,
            badge: {
                badgeTypeId: this.form.$.badgeTypeId.value!,
                ...(badgeLevelIndex > 0 && { badgeLevelIndex }),
                badgeName: this.form.$.badgeName.value,
                badgeImageUrl: this.form.$.badgeImageUrl.value
            }
        };
    }

    /**
     * Snapshot the new badge form data for use after challenge creation.
     * Must be called BEFORE createChallengeRequest() to avoid MobX reaction race.
     */
    public snapshotNewBadgeData() {
        if (this.form.$.rewardType.value !== 'NEW_BADGE') {
            return undefined;
        }
        return {
            name: this.form.$.newBadgeName.value,
            description: this.form.$.newBadgeDescription.value,
            image: this.form.$.newBadgeImage.value
        };
    }

    /**
     * Create a challenge badge from a pre-captured snapshot.
     * Called AFTER challenge creation with the snapshot captured before.
     */
    public async createChallengeBadgeFromSnapshot(
        snapshot: { name: I18NText; description?: I18NText; image?: CdnObjectDto },
        challengeId: number,
        communityId: number
    ) {
        const response = await this.fitnessActivityClient.admin
            .badgeTypeAdminControllerCreateChallengeBadge({
                name: snapshot.name as Record<string, string>,
                description: snapshot.description as Record<string, string> | undefined,
                image: snapshot.image,
                challengeId,
                communityId
            });
        this.createdBadgeTypeId = response.data.id;

        const imageUrl = snapshot.image
            ? getCdnImage(snapshot.image.bucket, snapshot.image.key)
            : undefined;

        return {
            rewardType: RewardType.BADGE,
            badge: {
                badgeTypeId: response.data.id,
                badgeLevelIndex: 1,
                badgeName: snapshot.name,
                badgeImageUrl: imageUrl
            }
        } as ChallengeRewardConfigDto;
    }

    /**
     * Update an existing challenge badge (name, description, image).
     */
    public async updateChallengeBadge(badgeTypeId: number) {
        const name = this.form.$.newBadgeName.value;
        const description = this.form.$.newBadgeDescription.value;
        const image = this.form.$.newBadgeImage.value;

        await this.fitnessActivityClient.admin
            .badgeTypeAdminControllerUpdateChallengeBadge(badgeTypeId, {
                name: name as Record<string, string> | undefined,
                description: description as Record<string, string> | undefined,
                image
            });

        const imageUrl = image
            ? getCdnImage(image.bucket, image.key)
            : this.form.$.badgeImageUrl.value;

        return {
            rewardType: RewardType.BADGE,
            badge: {
                badgeTypeId,
                badgeLevelIndex: 1,
                badgeName: name,
                badgeImageUrl: imageUrl
            }
        } as ChallengeRewardConfigDto;
    }

    @bind
    public async updateReward() {
        const validation = await this.validateReward();
        if (validation) {
            this.state = StepStatus.ERROR;
            return;
        }

        const challengeId = this.challengeDataStore.challengeId;
        if (challengeId && this.formIsDirty) {
            this.state = StepStatus.UPDATING;

            const rewardType = this.form.$.rewardType.value;

            if (rewardType === 'NEW_BADGE') {
                let rewardConfig: ChallengeRewardConfigDto;
                if (this.createdBadgeTypeId) {
                    // Update existing challenge badge
                    rewardConfig = await this.updateChallengeBadge(
                        this.createdBadgeTypeId
                    );
                } else {
                    // Create new challenge badge in edit mode
                    const communityId = this.challengeDataStore.communityId;
                    if (!communityId) {
                        throw new Error('Community ID is required to create a challenge badge');
                    }
                    const snapshot = this.snapshotNewBadgeData();
                    if (!snapshot) {
                        throw new Error('Badge data is required');
                    }
                    rewardConfig = await this.createChallengeBadgeFromSnapshot(
                        snapshot,
                        challengeId,
                        communityId
                    );
                }
                await this.rewardUpdateRequest.request({
                    challengeId,
                    data: rewardConfig
                });
            } else if (rewardType === 'BADGE') {
                const rewardConfig = this.generateRequestData();
                if (rewardConfig) {
                    await this.rewardUpdateRequest.request({
                        challengeId,
                        data: rewardConfig
                    });
                }
            } else {
                await this.rewardUpdateRequest.request({
                    challengeId,
                    data: { rewardType: RewardType.BADGE, badge: undefined }
                });
            }
        }
    }

    public rewardUpdateRequest = wrapRequest(
        async ({
            challengeId,
            data
        }: {
            challengeId: number;
            data: ChallengeRewardConfigDto;
        }) => {
            const result =
                await this.challengeClient?.support.challengeSupportControllerUpdateReward(
                    challengeId,
                    data
                );

            return result?.data;
        }
    );

    @bind
    public async updateForm(rewardConfig?: ChallengeRewardConfigDto) {
        this.form = this.generateForm();
        this.createdBadgeTypeId = undefined;

        if (rewardConfig?.rewardType === RewardType.BADGE && rewardConfig.badge) {
            const badgeTypeId = rewardConfig.badge.badgeTypeId;

            // Detect challenge badges by fetching the badge type
            let isChallengeBadge = false;
            try {
                const response = await this.fitnessActivityClient.admin
                    .badgeTypeAdminControllerGetType(badgeTypeId);
                isChallengeBadge = response.data.challengeId != null;
            } catch {
                // If fetch fails, treat as regular badge
            }

            if (isChallengeBadge) {
                this.createdBadgeTypeId = badgeTypeId;
                this.form.$.rewardType.onChange('NEW_BADGE');
                this.form.$.newBadgeName.onChange(
                    rewardConfig.badge.badgeName || {}
                );
                this.form.$.badgeImageUrl.onChange(
                    rewardConfig.badge.badgeImageUrl
                );
            } else {
                this.form.$.rewardType.onChange('BADGE');
            }

            this.form.$.badgeTypeId.onChange(badgeTypeId);
            this.form.$.badgeLevelIndex.onChange(
                rewardConfig.badge.badgeLevelIndex ?? 0
            );
            this.form.$.badgeName.onChange(rewardConfig.badge.badgeName);
            this.form.$.badgeImageUrl.onChange(
                rewardConfig.badge.badgeImageUrl
            );

            const name = rewardConfig.badge.badgeName;
            this.selectedBadgeLabel = name?.en || '';
        }

        this.state = StepStatus.COMPLETED;
        makeFormClean(this.form);
    }

    @autodisposer.tsdi
    public initialize() {
        return [
            reaction(
                () => this.challengeDataStore.challengeConfiguration,
                async (configuration) => {
                    await this.updateForm(configuration?.rewardConfig);
                }
            )
        ];
    }
}
